import { useMutation, useQueryClient } from "@tanstack/react-query"
import { fetcher } from "../fetcher"
import { removeValue, retrieveValue, storeValue } from "../../utils"
import { DEMO_PHONE_NUMBER } from "../../constants"
import { usePostHog } from "../../hooks"
import { useFetchPersonalData } from "../queries"
import { Sentry } from "../../utils/Sentry"

export type ConfirmPhoneAuthResponse = {
  status: string
  next: "Login" | "Onboarding" | null
  refreshToken: string | null
  referenceId: string | null
  verificationId: string | null
  redirectUrl: string | null
}

type MutationFnParams = {
  attemptId: string
  code: string
  phoneNumber: string
  disableErrorScreen?: boolean
}

export function useConfirmPhoneAuth() {
  const queryClient = useQueryClient()
  const posthog = usePostHog()
  const fetchPersonalData = useFetchPersonalData()

  return useMutation<ConfirmPhoneAuthResponse, unknown, MutationFnParams>({
    mutationFn: async ({ attemptId, code, phoneNumber, disableErrorScreen }) => {
      if (
        phoneNumber.replaceAll("-", "") === DEMO_PHONE_NUMBER &&
        (await retrieveValue("mode")) === "demo"
      ) {
        await storeValue("refreshToken", "demo-token")
      } else {
        await removeValue("mode")
      }

      const response = await fetcher(
        "/Auth/Phone/Confirm",
        {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            attemptId,
            code,
          }),
        },
        { disableErrorScreen }
      )

      return response.json()
    },
    onSuccess: (response) => {
      if (response.verificationId) {
        queryClient.setQueryData(["phoneNumberVerificationId"], response.verificationId)
      }
      if (response.refreshToken) {
        storeValue("refreshToken", response.refreshToken)
      }
      if (response.next === "Login" && response.referenceId) {
        retrieveValue("accessToken").then((value) => {
          if (!value) {
            return
          }
          fetchPersonalData().then((pd) => {
            posthog.identify(response.referenceId!, {
              name: `${pd.firstName} ${pd.lastName}`,
              firstName: pd.firstName,
              lastName: pd.lastName,
              email: pd.emailAddress,
            })
          })
        })
      } else if (response.referenceId) {
        posthog.identify(response.referenceId)
      }
    },
    onError: (error) => {
      Sentry.captureException(error, { tags: { source: "useConfirmPhoneAuth" } })
    },
  })
}
